import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import {
  InvitePanel,
  LinksPanel,
  RequestsList,
  useCustomRoles,
  useWorkspaceRequests,
} from '@/entities/collab';
import type { Workspace } from '@/entities/workspace';
import { WorkspacePalettes } from '@/features/color-palettes';
import { can, PERMISSIONS } from '@/shared/lib/permissions';
import { Icons } from '@/shared/ui';

import styles from './Admin.module.css';
import { assignableStandardRoles, isGrantableRole } from './grantable-roles';
import { type AdminTab, visibleTabs } from './tabs';
import { AuditTab } from './AuditTab';
import { MembersTab } from './MembersTab';
import { NotificationsTab } from './NotificationsTab';
import { RolesTab } from './RolesTab';
import { SettingsTab } from './SettingsTab';
import { TeamsTab } from './TeamsTab';

const ICONS: Record<AdminTab, () => React.JSX.Element> = {
  members: Icons.users,
  teams: Icons.layers,
  roles: Icons.shield,
  invitations: Icons.mail,
  requests: Icons.inbox,
  notifications: Icons.bell,
  audit: Icons.history,
  palettes: Icons.palette,
  settings: Icons.sliders,
};

/** Workspace invitations and links: the standard roles and custom roles the viewer may grant. */
function InvitationsTab({ workspace }: { workspace: Workspace }) {
  const roles = useCustomRoles(workspace.id);
  const standard = assignableStandardRoles(workspace);
  const custom = (roles.data ?? []).filter((role) => isGrantableRole(workspace, role));
  const scope = { kind: 'workspace', id: workspace.id } as const;
  return (
    <div className={styles.panel}>
      <InvitePanel scope={scope} allowedRoles={standard} customRoles={custom} />
      <LinksPanel scope={scope} allowedRoles={standard} customRoles={custom} />
    </div>
  );
}

function RequestsTab({ workspace }: { workspace: Workspace }) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const resolved = params.get('resolved') === '1';
  const requests = useWorkspaceRequests(workspace.id, resolved);
  const canResolve =
    can(workspace.permissions, PERMISSIONS.workspaceReviewRequests) ||
    can(workspace.permissions, PERMISSIONS.workspaceReviewTransfers);
  return (
    <div className={styles.panel}>
      <label className={styles.form}>
        <input
          type="checkbox"
          checked={resolved}
          onChange={(event) => {
            const next = new URLSearchParams(params);
            if (event.target.checked) next.set('resolved', '1');
            else next.delete('resolved');
            setParams(next, { replace: true });
          }}
        />
        <span>{t('workspaceAdmin.showResolved')}</span>
      </label>
      <RequestsList requests={requests.data ?? []} canResolve={canResolve} />
    </div>
  );
}

/**
 * Administration of a team workspace as a management area: a side navigation (a collapsible
 * list on phones) and the section on its right. Which sections and controls appear follows the
 * permissions the server reported; the server enforces every action again. The section is kept
 * in the URL (`?tab=`), so a notification or a bookmark opens it directly.
 */
export function WorkspaceAdmin({ workspace }: { workspace: Workspace }) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [navOpen, setNavOpen] = useState(false);
  const tabs = visibleTabs(workspace);
  const requested = params.get('tab');
  const tab = tabs.find((value) => value === requested) ?? 'members';
  const Icon = ICONS[tab];
  return (
    <section className={styles.admin} aria-label={t('workspaceAdmin.title')}>
      <div className={styles.layout}>
        <nav className={styles.sidebar} aria-label={t('workspaceAdmin.sections')}>
          <button
            type="button"
            className={styles.navToggle}
            aria-expanded={navOpen}
            aria-controls="admin-nav-list"
            onClick={() => {
              setNavOpen((open) => !open);
            }}
          >
            <Icon />
            <span className={styles.navCurrent}>{t(`workspaceAdmin.tabs.${tab}`)}</span>
            <Icons.chevronDown />
          </button>
          <ul
            id="admin-nav-list"
            className={styles.navList}
            data-open={navOpen ? 'true' : undefined}
          >
            {tabs.map((value) => {
              const TabIcon = ICONS[value];
              return (
                <li key={value}>
                  <button
                    type="button"
                    aria-current={tab === value ? 'page' : undefined}
                    className={`${styles.navItem} ${tab === value ? styles.navItemOn : ''}`}
                    onClick={() => {
                      const next = new URLSearchParams(params);
                      next.set('tab', value);
                      next.delete('resolved');
                      setParams(next, { replace: true });
                      setNavOpen(false);
                    }}
                  >
                    <TabIcon />
                    <span>{t(`workspaceAdmin.tabs.${value}`)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className={styles.content}>
          <h2 className={styles.sectionHeading}>{t(`workspaceAdmin.tabs.${tab}`)}</h2>
          {tab === 'members' && <MembersTab workspace={workspace} />}
          {tab === 'teams' && <TeamsTab workspace={workspace} />}
          {tab === 'roles' && <RolesTab workspace={workspace} />}
          {tab === 'invitations' && <InvitationsTab workspace={workspace} />}
          {tab === 'requests' && <RequestsTab workspace={workspace} />}
          {tab === 'notifications' && <NotificationsTab workspace={workspace} />}
          {tab === 'audit' && <AuditTab workspace={workspace} />}
          {tab === 'palettes' && (
            <WorkspacePalettes
              workspaceId={workspace.id}
              isTeam={workspace.workspace_type === 'team'}
            />
          )}
          {tab === 'settings' && <SettingsTab workspace={workspace} />}
        </div>
      </div>
    </section>
  );
}
